import { useAuthStore } from '@/features/auth';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { memo, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useMessagePinned } from '../../queries/use-message-pinned';
import { MessageListPinnedItem } from './message-list-pinned-item';
interface MessageListPinnedPopupProps {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  onMessageClick?: (messageId: string) => void;
}

export const PinnedMessagesPopup = memo<MessageListPinnedPopupProps>(
  ({ open, onClose, conversationId, onMessageClick }) => {
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useMessagePinned(
      conversationId,
      20
    );
    const userId = useAuthStore((s) => s.user?.user_id);

    const allMessages = data?.pages.flatMap((page) => page.pinnedMessages) || [];

    const handleMessageClick = useCallback(
      (messageId: string) => {
        onMessageClick?.(messageId);
        onClose();
      },
      [onMessageClick, onClose]
    );

    const handleLoadMore = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const itemContent = useCallback(
      (index: number) => {
        const message = allMessages[index];
        if (!message) return null;

        return (
          <MessageListPinnedItem
            curUserId={userId || ''}
            key={message.messageId}
            message={message}
            onClick={handleMessageClick}
            showPinnedInfo={true}
          />
        );
      },
      [allMessages, handleMessageClick, hasNextPage]
    );

    const footer = useCallback(() => {
      if (!hasNextPage) return null;

      return (
        <Box className='mex:flex mex:justify-center mex:p-4'>
          <Button
            onClick={handleLoadMore}
            disabled={isFetchingNextPage}
            variant='outlined'
            size='small'
            startIcon={isFetchingNextPage && <CircularProgress size={16} />}
          >
            {isFetchingNextPage ? 'Đang tải...' : 'Tải thêm'}
          </Button>
        </Box>
      );
    }, [hasNextPage, handleLoadMore, isFetchingNextPage]);

    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth='sm'
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              height: '80vh',
              maxHeight: '600px'
            }
          }}
        >
          <DialogTitle className='mex:flex mex:items-center mex:justify-between'>
            <Typography variant='h6' component='div'>
              📌 Tin nhắn đã ghim
            </Typography>
          </DialogTitle>

          <DialogContent className='mex:!p-0 mex:flex mex:flex-col'>
            {isLoading ? (
              <Box className='mex:flex mex:justify-center mex:p-8'>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box className='mex:flex mex:flex-col mex:items-center mex:justify-center mex:p-8'>
                <Typography variant='body2' color='error' className='mex:mb-4'>
                  Không thể tải tin nhắn đã ghim
                </Typography>
                <Button variant='outlined' onClick={() => window.location.reload()}>
                  Thử lại
                </Button>
              </Box>
            ) : allMessages.length === 0 ? (
              <Box className='mex:flex mex:flex-col mex:items-center mex:justify-center mex:p-8'>
                <Typography variant='body2' color='text.secondary' className='mex:mb-2'>
                  📌 Chưa có tin nhắn nào được ghim
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Nhấn và giữ tin nhắn để ghim
                </Typography>
              </Box>
            ) : (
              <Virtuoso
                style={{ height: '100%' }}
                totalCount={allMessages.length}
                itemContent={itemContent}
                components={{
                  Footer: footer
                }}
                endReached={() => {
                  if (hasNextPage && !isFetchingNextPage) {
                    handleLoadMore();
                  }
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }
);

PinnedMessagesPopup.displayName = 'PinnedMessagesPopup';
